/**
 * mortgageContext.js
 * Shared state for mortgage calculator via Context + useReducer.
 * Pattern follows BudgetContext.jsx from features/budgetPlanner/context/
 */
import React, { createContext, useContext, useReducer, useMemo } from 'react';
import {
  calculateMortgage,
  paymentsPerYearFromFreq,
  getMortgageInsuranceRate,
  calculateMinimumDownPayment,
  convertPaymentFrequency,
  getLowestPromoRates,
} from './mortgageCalcEngine';

/* ── Initial state ── */
const initialState = {
  homePrice: 500000,
  downPayment: 25000,
  amortization: 25,
  loanTerm: 5,
  interestRate: 0, // auto-filled from lowest rate
  rateType: 'fixed', // 'fixed' | 'variable'
  paymentFrequency: 'monthly',
  province: 'ON',
  purposeType: 'purchase', // 'purchase' | 'refinance' | 'renovate'
  propertyType: 'detached',
  municipalTaxEstimate: 0,
  lumpSumAmount: 0,
  extraPaymentPerPeriod: 0,
  compareSelection: [], // max 2 bank names
};

/* ── Reducer ── */
function mortgageReducer(state, action) {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };

    case 'SET_RATE_FROM_TABLE':
      return {
        ...state,
        interestRate: action.rate,
        rateType: action.rateType || state.rateType,
      };

    case 'SET_COMPARE': {
      const sel = state.compareSelection;
      if (sel.includes(action.bankName)) return state;
      if (sel.length >= 2) return state;
      return { ...state, compareSelection: [...sel, action.bankName] };
    }

    case 'TOGGLE_COMPARE': {
      const sel = state.compareSelection;
      if (sel.includes(action.bankName)) {
        return { ...state, compareSelection: sel.filter((b) => b !== action.bankName) };
      }
      if (sel.length >= 2) return state;
      return { ...state, compareSelection: [...sel, action.bankName] };
    }

    case 'CLEAR_COMPARE':
      return { ...state, compareSelection: [] };

    case 'HYDRATE':
      return { ...state, ...action.payload };

    case 'RESET':
      return { ...initialState, interestRate: state.interestRate };

    default:
      return state;
  }
}

/* ── Context ── */
const MortgageContext = createContext(null);

export function MortgageProvider({ children, mortgageData }) {
  // Auto-fill lowest rate
  const defaultRate = useMemo(() => {
    if (!mortgageData) return 4.5;
    const fixed5 = getLowestPromoRates(mortgageData, '5-Year Fixed', 'fixedRates', 1);
    return fixed5[0]?.rate || 4.5;
  }, [mortgageData]);

  const [state, dispatch] = useReducer(mortgageReducer, {
    ...initialState,
    interestRate: defaultRate,
  });

  // Derived values via useMemo
  const derived = useMemo(() => {
    const ppy = paymentsPerYearFromFreq(state.paymentFrequency);
    const result = calculateMortgage({
      homePrice: state.homePrice,
      downPayment: state.downPayment,
      annualRate: state.interestRate / 100,
      amortYears: state.amortization,
      paymentsPerYear: ppy,
      lumpSumAmount: state.lumpSumAmount,
      extraPaymentPerPeriod: state.extraPaymentPerPeriod,
      loanTerm: state.loanTerm,
    });

    if (result.error) {
      return {
        error: result.error,
        monthlyPayment: 0,
        biweeklyPayment: 0,
        totalLoan: 0,
        cmhcRequired: false,
        cmhcAmount: 0,
        downPaymentPercent: 0,
        totalInterestAmort: 0,
        totalInterestTerm: 0,
        totalCostAmort: 0,
        principalAmount: 0,
      };
    }

    // Calculate monthly regardless of frequency
    const monthlyResult = calculateMortgage({
      homePrice: state.homePrice,
      downPayment: state.downPayment,
      annualRate: state.interestRate / 100,
      amortYears: state.amortization,
      paymentsPerYear: 12,
      loanTerm: state.loanTerm,
    });

    const monthlyPayment = monthlyResult.error ? 0 : monthlyResult.paymentPerPeriod;
    const biweeklyPayment = convertPaymentFrequency(monthlyPayment, 'bi-weekly');
    const minDP = calculateMinimumDownPayment(state.homePrice);

    return {
      monthlyPayment,
      biweeklyPayment,
      paymentPerPeriod: result.paymentPerPeriod,
      totalLoan: result.totalLoan,
      principalAmount: result.principalAmount,
      cmhcRequired: result.cmhcRequired,
      cmhcAmount: result.mortgageInsurance,
      cmhcRate: result.cmhcRate,
      downPaymentPercent: result.downPaymentPercent,
      totalInterestAmort: result.totalInterestAmort,
      totalInterestTerm: result.totalInterestTerm,
      totalCostAmort: result.totalCostAmort,
      minDownPayment: minDP,
      insuranceRate: getMortgageInsuranceRate(result.downPaymentPercent),
      effectiveAnnualRate: result.effectiveAnnualRate,
      ratePerPeriod: result.ratePerPeriod,
      paymentsPerYear: result.paymentsPerYear,
      numberOfPaymentsAmortization: result.numberOfPaymentsAmortization,
      numberOfPaymentsTerm: result.numberOfPaymentsTerm,
      annualRate: result.annualRate,
    };
  }, [
    state.homePrice,
    state.downPayment,
    state.interestRate,
    state.amortization,
    state.paymentFrequency,
    state.lumpSumAmount,
    state.extraPaymentPerPeriod,
    state.loanTerm,
  ]);

  const value = useMemo(
    () => ({ state, dispatch, derived, mortgageData }),
    [state, derived, mortgageData],
  );

  return (
    <MortgageContext.Provider value={value}>
      {children}
    </MortgageContext.Provider>
  );
}

export function useMortgage() {
  const ctx = useContext(MortgageContext);
  if (!ctx) throw new Error('useMortgage must be used within MortgageProvider');
  return ctx;
}
